import { getIn } from "@utils/index"
import { getMarketAmount } from "@utils/pricing"
import * as R from "ramda"
import React from "react"

export enum SortMethods {
  ByBestSelling = "BEST_SELLING",
  ByFeatured = "BY_FEATURED",
  ByPrice = "BY_PRICE",
  ByPriceReversed = "BY_PRICE_REVERSED",
  ByRecent = "BY_RECENT",
}

type Args = {
  customerCountryCode?: string
  isCustomCollection?: boolean
  pinnedProducts?: any[]
}

const newestSort = (a, b) => {
  const aDate = new Date(getIn(a, "createdAt", 0))
  const bDate = new Date(getIn(b, "createdAt", 0))
  return (
    (((bDate as any) > (aDate as any)) as any) -
    (((bDate as any) < (aDate as any)) as any)
  )
}

// @ts-expect-error Ignore
const bestSellingSort = R.descend(R.prop("priority"))
// @ts-expect-error Ignore
const productSubTypeSort = R.descend(R.prop("subTypePriority"))

/**
 * Compose multiple comparator functions into a single comparator
 */
const composeComparators =
  (...fns: Array<(a: any, b: any) => number>) =>
  (a: any, b: any) => {
    for (const fn of fns) {
      const r = fn(a, b)
      if (r !== 0) return r
    }
    return 0
  }

/**
 * Manages sorting logic for collection pages
 */
export function useCollectionSorting({
  customerCountryCode = "US",
  isCustomCollection = false,
  pinnedProducts = [],
}: Args) {
  const [sortMethod, setSortMethod] = React.useState<any>(
    isCustomCollection ? SortMethods.ByFeatured : SortMethods.ByRecent
  )

  const dropdownHandler = (sortMethod) => {
    setSortMethod(sortMethod)
  }

  const dropdownOptions = {
    isCustomCollection,
    options: [
      { name: "Featured", sort: SortMethods.ByFeatured },
      { name: "Price Low to High", sort: SortMethods.ByPrice },
      { name: "Price High to Low", sort: SortMethods.ByPriceReversed },
      { name: "Newest", sort: SortMethods.ByRecent },
      { name: "Best Selling", sort: SortMethods.ByBestSelling },
    ],
    setSort: dropdownHandler,
    sortMethod,
  }

  const pinnedProductSort = React.useMemo(() => {
    return (a, b) => {
      const indexA = pinnedProducts.findIndex(({ slug }) => slug === a.slug)
      const indexB = pinnedProducts.findIndex(({ slug }) => slug === b.slug)

      return indexB - indexA
    }
  }, [pinnedProducts])

  const priceSortWithLocation = React.useCallback(
    (a, b) => {
      const priceA = getMarketAmount(
        a.variants[0],
        customerCountryCode,
        "price"
      )
      const priceB = getMarketAmount(
        b.variants[0],
        customerCountryCode,
        "price"
      )
      return priceA - priceB
    },
    [customerCountryCode]
  )

  const priceSortReversedWithLocation = React.useCallback(
    (a, b) => {
      const priceA = getMarketAmount(
        a.variants[0],
        customerCountryCode,
        "price"
      )
      const priceB = getMarketAmount(
        b.variants[0],
        customerCountryCode,
        "price"
      )
      return priceB - priceA
    },
    [customerCountryCode]
  )

  const sortMethodsMap = React.useMemo(
    () => ({
      [SortMethods.ByBestSelling]: [pinnedProductSort, bestSellingSort],
      [SortMethods.ByFeatured]: [pinnedProductSort],
      [SortMethods.ByPrice]: [pinnedProductSort, priceSortWithLocation],
      [SortMethods.ByPriceReversed]: [
        pinnedProductSort,
        priceSortReversedWithLocation,
      ],
      [SortMethods.ByRecent]: [
        pinnedProductSort,
        productSubTypeSort,
        newestSort,
      ],
    }),
    [pinnedProductSort, priceSortWithLocation, priceSortReversedWithLocation]
  )

  const getSortedProducts = React.useCallback(
    (products: any[]) => {
      const comparators = sortMethodsMap[sortMethod] ?? []
      const comparator = composeComparators(...comparators)
      return [...products].sort(comparator)
    },
    [sortMethod, sortMethodsMap]
  )

  return {
    dropdownOptions,
    getSortedProducts,
    sortMethod,
  }
}
